import React, { useCallback, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import "../styles/ThemeToggle.css";

const STORAGE_KEY = "vv-theme";

const readStoredTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "dark" || saved === "light" ? saved : null;
  } catch {
    return null;
  }
};

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const labels = {
  en: { toDark: "Switch to dark mode", toLight: "Switch to light mode" },
  de: { toDark: "Zu dunklem Modus wechseln", toLight: "Zu hellem Modus wechseln" },
};

export const ThemeToggle = () => {
  const { language } = useLanguage();
  // null = der Systemeinstellung folgen
  const [theme, setTheme] = useState(readStoredTheme);

  const isDark = theme ? theme === "dark" : systemPrefersDark();

  // Solange nichts gewählt wurde, dem System folgen – auch wenn es sich ändert.
  useEffect(() => {
    if (theme) return undefined;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setTheme(null);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [theme]);

  const toggle = useCallback(() => {
    const next = isDark ? "light" : "dark";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Storage gesperrt – Wechsel gilt nur für diese Sitzung */
    }
  }, [isDark]);

  const label = (labels[language] || labels.en)[isDark ? "toLight" : "toDark"];

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={label}
      title={label}
      aria-pressed={isDark}
    >
      {isDark ? (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.6v2.6M12 18.8v2.6M2.6 12h2.6M18.8 12h2.6M5.3 5.3l1.9 1.9M16.8 16.8l1.9 1.9M18.7 5.3l-1.9 1.9M7.2 16.8l-1.9 1.9" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M20.2 14.5A8.4 8.4 0 0 1 9.5 3.8a8.4 8.4 0 1 0 10.7 10.7Z" />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
